import { FC, ReactNode, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import BPIconButton from '../icons/BPIconButton';
import { cn } from 'lib/utils';
import BPGradientBox from '../box/BPGradientBox';
import BPRainbowBox from '../box/BPRainbowBox';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { Flip } from 'gsap/Flip';

gsap.registerPlugin(Flip);

type WidthLimit = number | null;
interface BPPopupProps {
  children: ReactNode;
  allowBackgroundClick?: boolean;
  passThrough?: boolean;
  padSides?: boolean;
  padding?: string;
  scrollBoxClasses?: string;
  rainbowEdge?: boolean;
  cycleRainbow?: boolean;
  fillScreenHeight?: boolean;
  showBorder?: boolean;
  showCornerCloseButton?: boolean;
  handleClose?: () => void;
  widthLimit?: WidthLimit;
  animateExpand?: boolean;
}

const BPPopup: FC<BPPopupProps> = ({
  children,
  allowBackgroundClick = true,
  passThrough = false,
  padSides = true,
  padding = undefined,
  scrollBoxClasses = '',
  rainbowEdge = false,
  cycleRainbow = false,
  fillScreenHeight = false,
  showBorder = true,
  showCornerCloseButton = true,
  handleClose = () => {},
  widthLimit = 1100,
  animateExpand = false,
}) => {
  return (
    <>
      {passThrough
        ? children
        : createPortal(
            <Container>
              <ClickScreen
                allowBackgroundClick={allowBackgroundClick}
                handleClose={handleClose}
              />
              <ScrollBoxContainer widthLimit={widthLimit}>
                <ScrollBox
                  padSides={padSides}
                  padding={padding}
                  showBorder={showBorder}
                  rainbowEdge={rainbowEdge}
                  cycleRainbow={cycleRainbow}
                  fillScreenHeight={fillScreenHeight}
                  animateExpand={animateExpand}
                  showCornerCloseButton={showCornerCloseButton}
                  scrollBoxClasses={scrollBoxClasses}
                  handleClose={handleClose}
                >
                  {children}
                </ScrollBox>
              </ScrollBoxContainer>
            </Container>,
            document.body
          )}
    </>
  );
};

const Container: FC<{ children: ReactNode }> = ({ children }) => (
  <div className="flex fixed place-content-center top-0 left-0 w-full h-full z-30 backdrop-blur-md bg-black/40 animate-fade-in-fast pointer-events-none">
    {children}
  </div>
);

const ScrollBoxContainer: FC<{
  children: ReactNode;
  widthLimit: WidthLimit;
}> = ({ children, widthLimit = null }) => (
  <div
    className="flex relative w-full h-full max-h-[100%] place-content-center flex-col px-5"
    style={{
      maxWidth: widthLimit ? `${widthLimit}px` : 'none',
    }}
  >
    {children}
  </div>
);

interface ScrollBoxProps {
  children: ReactNode;
  padSides?: boolean;
  padding?: string;
  showBorder?: boolean;
  rainbowEdge?: boolean;
  cycleRainbow?: boolean;
  fillScreenHeight?: boolean;
  animateExpand?: boolean;
  showCornerCloseButton?: boolean;
  scrollBoxClasses?: string;
  handleClose?: () => void;
}

const ScrollBox: FC<ScrollBoxProps> = ({
  children,
  padSides = true,
  padding = undefined,
  showBorder = true,
  rainbowEdge = false,
  cycleRainbow = false,
  fillScreenHeight = false,
  animateExpand = false,
  showCornerCloseButton = true,
  scrollBoxClasses = '',
  handleClose = () => {},
}) => {
  const scrollBox = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState<boolean>(!animateExpand);
  const [showContents, setShowContents] = useState<boolean>(!animateExpand);
  const [flipState, setFlipState] = useState<Flip.FlipState>(
    {} as Flip.FlipState
  );

  useGSAP(() => {
    if (!scrollBox.current) return;
    const state = Flip.getState(scrollBox.current);
    setFlipState(state);
    setExpanded(true);
  }, [scrollBox]);

  useGSAP(() => {
    if (!flipState || !expanded) return;
    Flip.from(flipState, {
      duration: 0.5,
      delay: 0.2,
      ease: 'power3.inOut',
      onComplete: () => setShowContents(true),
    });
  }, [flipState, expanded]);

  return (
    <div
      ref={scrollBox}
      className={cn(
        'block relative top-0 left-0 my-8 overflow-hidden rounded-lg backdrop-blur-sm bg-black/40 pointer-events-auto mx-auto',
        expanded ? (fillScreenHeight ? 'h-full' : 'h-auto') : 'h-[80px]',
        expanded ? 'w-full' : 'w-[80px]',
        scrollBoxClasses ? scrollBoxClasses : ''
      )}
    >
      {showBorder ? (
        rainbowEdge ? (
          <BPRainbowBox
            showBg={false}
            showGlow={false}
            cycleHue={cycleRainbow}
          />
        ) : (
          <BPGradientBox />
        )
      ) : null}
      <div
        className={cn(
          'block relative top-0 left-0 h-full overflow-y-auto scrolling-touch pointer-events-auto',
          padSides && padding
            ? padding
            : padSides
              ? 'px-[40px] lg:px-[80px] py-[40px] lg:py-[40px] '
              : 'p-0'
        )}
      >
        {children}
      </div>
      <div
        className={cn(
          'block absolute top-[1px] left-[1px] w-[calc(100%-2px)] h-[calc(100%-2px)] rounded-[8px] bg-bionic-greyDark transition-opacity duration-200 pointer-events-none',
          showContents ? 'opacity-0' : 'opacity-100'
        )}
      />
      {showCornerCloseButton && showContents && (
        <div className="block absolute top-[20px] right-[20px] z-10 animate-fade-in">
          <BPIconButton icon="close" onClick={handleClose} />
        </div>
      )}
    </div>
  );
};

const ClickScreen: FC<{
  allowBackgroundClick: boolean;
  handleClose: () => void;
}> = ({ allowBackgroundClick, handleClose }) => (
  <div
    className="block absolute top-0 left-0 w-full h-full pointer-events-auto"
    onClick={allowBackgroundClick ? handleClose : undefined}
  />
);

export default BPPopup;
